import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Sparkles, TrendingUp, Award, Flame, Star, Crown, Heart, Plus, Lock,
  Trophy, CheckCircle2, Circle, ArrowUpRight, Images, ClipboardList, Clock
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fileUrl } from "@/api";
import { useAuth } from "@/context/AuthContext";
import PaywallModal from "@/components/PaywallModal";

const BADGE_ICONS = { sparkles: Sparkles, star: Star, heart: Heart, flame: Flame, crown: Crown };

const ADMIN_MODE_KEY = "nail_rater_admin_mode";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="glass rounded-2xl p-4 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className={`w-5 h-5 mx-auto mb-1 ${accent}`} />
      <p className="font-playfair text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function PremiumLock({ onUnlock, children, title }) {
  return (
    <div className="relative rounded-3xl overflow-hidden mb-6" data-testid="premium-lock">
      <div className="blur-sm pointer-events-none select-none">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/50 backdrop-blur-[2px] text-center p-6">
        <Crown className="w-8 h-8 text-amber-500 mb-2" />
        <p className="font-playfair text-lg font-bold text-foreground mb-1">{title}</p>
        <p className="text-sm text-muted-foreground mb-4 max-w-xs">Subscribe to unlock progress tracking, trend graphs, before/after, streaks, badges & weekly challenges.</p>
        <Button onClick={onUnlock} className="rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white" data-testid="premium-unlock-btn">
          <Crown className="w-4 h-4 mr-1" /> Go Premium
        </Button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isAdmin = localStorage.getItem(ADMIN_MODE_KEY) === "true";
  const subscribed = user?.is_subscribed || isAdmin;

  const [progress, setProgress] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [beforeId, setBeforeId] = useState("");
  const [afterId, setAfterId] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [p, a, c] = await Promise.all([
          api.get("/progress"),
          api.get("/analyses"),
          api.get("/challenges"),
        ]);
        setProgress(p.data);
        setAnalyses(a.data.analyses || []);
        setChallenges(c.data.challenges || []);
        const items = a.data.analyses || [];
        if (items.length >= 2) { setBeforeId(items[0].id); setAfterId(items[items.length - 1].id); }
        else if (items.length === 1) { setBeforeId(items[0].id); setAfterId(items[0].id); }
      } catch (e) {
        toast.error("Couldn't load your dashboard.");
      } finally {
        setLoadingData(false);
      }
    })();
  }, [user]);

  const chartData = useMemo(() => (progress?.history || []).map((h, i) => ({
    name: `#${i + 1}`, score: h.score, date: h.date,
  })), [progress]);

  const toggleChallenge = async (id) => {
    if (!subscribed) { setShowPaywall(true); return; }
    setChallenges((prev) => prev.map((c) => c.id === id ? { ...c, completed: !c.completed } : c));
    try { await api.post("/challenges/toggle", { challenge_id: id }); }
    catch (e) { toast.error("Couldn't update challenge."); }
  };

  const before = analyses.find((a) => a.id === beforeId);
  const after = analyses.find((a) => a.id === afterId);
  const latest = analyses[analyses.length - 1];

  if (loading || (user && loadingData)) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Sparkles className="w-10 h-10 text-primary animate-spin" /></div>;
  }
  if (!user) return null;

  return (
    <div className="min-h-screen px-4 py-8" data-testid="dashboard-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-muted-foreground">Welcome back</p>
            <h1 className="font-playfair text-3xl font-bold text-foreground">{user.name || user.email} 💅</h1>
          </div>
          <Button onClick={() => navigate("/")} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-analysis-btn">
            <Plus className="w-4 h-4 mr-1" /> New analysis
          </Button>
        </motion.div>

        {!subscribed && (
          <div className="glass rounded-2xl p-4 mb-6 flex items-center justify-between gap-4" data-testid="upsell-banner">
            <div className="flex items-center gap-2 text-sm text-foreground/80">
              <Crown className="w-5 h-5 text-amber-500" />
              {user.free_remaining > 0
                ? <>You have <b>{user.free_remaining}</b> free analysis left. Go Premium for unlimited coaching.</>
                : <>You've used your free analyses. Go Premium to keep tracking your progress.</>}
            </div>
            <Button onClick={() => setShowPaywall(true)} className="rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white shrink-0" data-testid="banner-upgrade-btn">Upgrade</Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Sparkles} label="Analyses" value={progress?.total_analyses ?? 0} accent="text-primary" />
          <StatCard icon={Trophy} label="Best Score" value={progress?.best_score ?? 0} accent="text-amber-500" />
          <StatCard icon={Flame} label="Streak" value={`${progress?.current_streak ?? 0}w`} accent="text-rose-500" />
          <StatCard icon={TrendingUp} label="Avg Score" value={progress?.average_score ?? 0} accent="text-green-500" />
        </div>

        {(progress?.total_analyses ?? 0) === 0 && (
          <div className="glass rounded-3xl p-8 text-center mb-6">
            <Sparkles className="w-10 h-10 text-primary mx-auto mb-3" />
            <p className="font-playfair text-xl font-bold text-foreground mb-1">No analyses yet</p>
            <p className="text-muted-foreground mb-4">Upload your first nail photo to start tracking your progress.</p>
            <Button onClick={() => navigate("/")} className="rounded-full bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-1" />Analyze my nails</Button>
          </div>
        )}

        {/* Premium sections */}
        {(progress?.total_analyses ?? 0) > 0 && (() => {
          const PremiumBody = (
            <div className="space-y-6">
              {/* Trend graph */}
              <div className="glass rounded-3xl p-6" data-testid="trend-section">
                <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Score Evolution</h3>
                {chartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(190,24,93,0.1)" />
                      <XAxis dataKey="name" stroke="#9d174d" fontSize={12} />
                      <YAxis domain={[0, 20]} stroke="#9d174d" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #fbcfe8" }} formatter={(v) => [`${v}/20`, "Score"]} labelFormatter={(l, p) => p?.[0]?.payload?.date ? format(new Date(p[0].payload.date), "MMM d, yyyy") : l} />
                      <Line type="monotone" dataKey="score" stroke="#BE185D" strokeWidth={3} dot={{ r: 4, fill: "#BE185D" }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground text-center py-8">Add at least 2 analyses to see your score trend 📈</p>}
              </div>

              {/* Before / After */}
              <div className="glass rounded-3xl p-6" data-testid="before-after-section">
                <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><Images className="w-5 h-5 text-primary" />Before / After</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <select value={beforeId} onChange={(e) => setBeforeId(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="before-select">
                    {analyses.map((a, i) => <option key={a.id} value={a.id}>#{i + 1} · {a.total_score}/20 · {format(new Date(a.created_at), "MMM d")}</option>)}
                  </select>
                  <select value={afterId} onChange={(e) => setAfterId(e.target.value)} className="rounded-xl border border-border bg-white px-3 py-2 text-sm" data-testid="after-select">
                    {analyses.map((a, i) => <option key={a.id} value={a.id}>#{i + 1} · {a.total_score}/20 · {format(new Date(a.created_at), "MMM d")}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[{ a: before, label: "Before" }, { a: after, label: "After" }].map(({ a, label }) => (
                    <div key={label} className="rounded-2xl overflow-hidden bg-secondary/30">
                      {a?.image_path ? <img src={fileUrl(a.image_path)} alt={label} className="w-full h-44 object-cover" /> : <div className="w-full h-44 flex items-center justify-center text-muted-foreground text-sm">No image</div>}
                      <div className="p-2 text-center"><p className="text-xs text-muted-foreground">{label}</p><p className="font-playfair font-bold text-primary">{a?.total_score ?? "-"}/20</p></div>
                    </div>
                  ))}
                </div>
                {before && after && <p className="text-center text-sm mt-3 font-medium">{after.total_score - before.total_score >= 0 ? <span className="text-green-600 inline-flex items-center gap-1"><ArrowUpRight className="w-4 h-4" />+{after.total_score - before.total_score} points improvement</span> : <span className="text-rose-600">{after.total_score - before.total_score} points</span>}</p>}
              </div>

              {/* Badges */}
              <div className="glass rounded-3xl p-6" data-testid="badges-section">
                <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" />Achievements</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {(progress?.badges || []).map((b) => {
                    const Icon = BADGE_ICONS[b.icon] || Award;
                    return (
                      <div key={b.id} className={`text-center rounded-2xl p-3 ${b.earned ? "bg-gradient-to-br from-pink-100 to-purple-100" : "bg-secondary/20 opacity-50"}`} title={b.desc} data-testid={`badge-${b.id}`}>
                        <Icon className={`w-7 h-7 mx-auto mb-1 ${b.earned ? "text-primary" : "text-muted-foreground"}`} />
                        <p className="text-[11px] font-medium text-foreground leading-tight">{b.title}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Weekly challenges */}
              <div className="glass rounded-3xl p-6" data-testid="challenges-section">
                <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><Flame className="w-5 h-5 text-rose-500" />This Week's Challenges</h3>
                <div className="space-y-2">
                  {challenges.map((c) => (
                    <button key={c.id} onClick={() => toggleChallenge(c.id)} className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-colors ${c.completed ? "bg-green-50" : "bg-secondary/20 hover:bg-secondary/40"}`} data-testid={`challenge-${c.id}`}>
                      {c.completed ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
                      <span className={`flex-1 text-sm ${c.completed ? "line-through text-muted-foreground" : "text-foreground"}`}>{c.title}</span>
                      <span className="text-xs text-primary font-semibold">+{c.points}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );

          return subscribed ? PremiumBody : <PremiumLock title="Unlock your Premium Dashboard" onUnlock={() => setShowPaywall(true)}>{PremiumBody}</PremiumLock>;
        })()}

        {/* Latest coaching plan */}
        {latest?.coaching && (
          <div className="glass rounded-3xl p-6 mb-6" data-testid="latest-coaching">
            <h3 className="font-playfair text-xl font-bold text-foreground mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" />Latest Coaching Plan</h3>
            <p className="font-semibold text-foreground mb-2">Recommendations</p>
            <ol className="space-y-2 mb-3">{(latest.coaching.recommendations || []).map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-foreground/80 text-sm"><span className="w-5 h-5 shrink-0 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">{i + 1}</span>{r}</li>
            ))}</ol>
            {latest.coaching.estimated_improvement_time && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4 text-primary" />Estimated time to improve: <span className="font-medium text-foreground">{latest.coaching.estimated_improvement_time}</span></p>}
            {latest.coaching.coach_message && <p className="italic text-foreground/80 bg-secondary/40 rounded-2xl p-4 mt-3 text-sm">💬 {latest.coaching.coach_message}</p>}
          </div>
        )}
      </div>

      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
    </div>
  );
}
